<template>
  <auth-layout>
    <h1 class="pop-auth__h1">{{ isSubAccount ? 'This email belongs to a POP sub-account' : 'This email already has a POP account' }}</h1>
    <p v-if="isSubAccount" class="pop-auth__sub" data-test="link-sub-account">
      <b>{{ email }}</b> is a sub-account on another POP account and signs in with a password. Link {{ providerName }}
      to it so both ways of logging in work, or just log in with the password. Plans are managed by the account owner.
    </p>
    <p v-else class="pop-auth__sub">
      <b>{{ email }}</b> signed up with a password. You can link {{ providerName }} to that account so both ways of
      logging in work, or just log in with the password.
    </p>

    <div class="account-exists__card">
      <img v-if="profile.pictureUrl" :src="profile.pictureUrl" alt="" class="account-exists__avatar" referrerpolicy="no-referrer" />
      <div v-else class="account-exists__avatar account-exists__avatar--initial">{{ initial }}</div>
      <div>
        <div class="account-exists__name">{{ profile.name || email }}</div>
        <div class="account-exists__email">{{ email }}</div>
      </div>
      <span class="account-exists__provider">{{ providerName }}</span>
    </div>

    <template v-if="mode === 'choose'">
      <div class="account-exists__choices">
        <button type="button" class="pop-auth__btn pop-auth__btn--primary" style="margin-top: 0" data-test="link-choose" @click="mode = 'link'">
          Link {{ providerName }} to this account
        </button>
        <router-link class="pop-auth__btn pop-auth__btn--secondary account-exists__secondary" :to="{ path: '/login', query: { email } }">
          Log in with password instead
        </router-link>
      </div>
    </template>
    <template v-else>
      <label class="pop-auth__label" for="link-password">Confirm your POP password to link</label>
      <password-field v-model="password" input-id="link-password" placeholder="Your existing password" autocomplete="current-password" :error="error" :disabled="locked" data-test="link-password" @input="error = ''" @enter="link" />
      <div class="pop-auth__hint">
        We ask once. After this, {{ providerName }} sign-in opens the same account.
        <router-link class="pop-auth__link" :to="{ path: '/forgotpassword', query: { email } }">Forgot it?</router-link>
      </div>
      <div v-if="locked" class="pop-auth__box pop-auth__box--danger">Too many attempts. Try again in {{ lockClock }}, or log in with your password later.</div>
      <button type="button" class="pop-auth__btn pop-auth__btn--primary" :disabled="busy || locked || password.length < 8" data-test="link-submit" @click="link">
        {{ busy ? 'Linking' : 'Link and log in' }}
      </button>
      <p class="pop-auth__fine pop-auth__center" style="margin-top: 14px">
        <button type="button" class="pop-auth__link" @click="mode = 'choose'">Back</button>
      </p>
    </template>
  </auth-layout>
</template>

<script>
import AuthLayout from '../AuthLayout.vue'
import PasswordField from '../PasswordField.vue'
import { finishLogin } from '../session'
import { getDeviceId } from '@/utils/device-id'
import { segmentPage, segmentTrack } from '@/utils'

export default {
  name: 'AccountExistsPage',
  components: { AuthLayout, PasswordField },
  data() {
    return { mode: 'choose', password: '', busy: false, error: '', lockedUntil: 0, now: Date.now(), ticker: null }
  },
  computed: {
    signup() {
      return this.$store.state.signup
    },
    profile() {
      return (this.signup.social && this.signup.social.profile) || {}
    },
    email() {
      return this.signup.email || this.profile.email || ''
    },
    isSubAccount() {
      return Boolean(this.signup.social && this.signup.social.isSubAccount)
    },
    providerName() {
      return this.profile.provider === 'apple' ? 'Apple' : 'Google'
    },
    initial() {
      return ((this.profile.name || this.email || 'P')[0] || 'P').toUpperCase()
    },
    locked() {
      return this.lockedUntil > this.now
    },
    lockClock() {
      const total = Math.max(0, Math.ceil((this.lockedUntil - this.now) / 1000))
      return total >= 60 ? `${Math.ceil(total / 60)} minutes` : `${total} seconds`
    },
  },
  created() {
    if (!this.signup.linkToken) {
      this.$router.replace('/register')
      return
    }
    segmentPage('SignupAccountExists', { provider: this.profile.provider })
    this.ticker = setInterval(() => { this.now = Date.now() }, 1000)
  },
  beforeDestroy() {
    clearInterval(this.ticker)
  },
  methods: {
    async link() {
      if (this.busy || this.locked || this.password.length < 8) return
      this.busy = true
      this.error = ''
      try {
        const response = await this.$store.dispatch('socialLink', {
          linkToken: this.signup.linkToken, password: this.password, deviceId: getDeviceId(),
        })
        const data = response.data || {}
        if (data.action === 'LOGIN') {
          segmentTrack('account_linked', { provider: this.profile.provider })
          const redirect = this.signup.redirect || ''
          this.$store.commit('signup/patch', { social: null, linkToken: '' })
          finishLogin(this, { token: data.token, user: data.user }, { event: 'login', eventData: { linked: this.profile.provider }, redirect })
        } else if (data.action === 'INVALID_CREDENTIALS') {
          const left = data.attemptsLeft
          this.error = left != null ? `Incorrect password. ${left} ${left === 1 ? 'attempt' : 'attempts'} left.` : 'Incorrect password.'
        } else if (data.action === 'LOCKED') {
          this.lockedUntil = Date.now() + (data.retryAfterSeconds || 900) * 1000
        } else if (data.action === 'PENDING_INVALID') {
          this.$notify({ group: 'info', type: 'warning', text: 'This sign-in expired. Please start again.' })
          this.$store.commit('signup/patch', { social: null, linkToken: '' })
          this.$router.replace('/login')
        } else {
          this.error = data.msg || 'Could not link your account. Please try again.'
        }
      } catch (e) {
        console.log(e)
        this.error = 'Could not link your account. Please try again.'
      } finally {
        this.busy = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import '../auth-tokens.scss';

.account-exists {
  &__card { display: flex; align-items: center; gap: 14px; padding: 16px; border: 1px solid $pop-field-border; border-radius: 12px; margin-bottom: 18px; }
  &__avatar { width: 44px; height: 44px; border-radius: 50%; flex: none; object-fit: cover; }
  &__avatar--initial { background: #316bff; color: #fff; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 18px; }
  &__name { font-weight: 800; font-size: 15px; color: $pop-teal; }
  &__email { font-size: 13px; color: $pop-ink-muted; }
  &__provider { margin-left: auto; font-size: 12px; color: $pop-ink-muted; font-weight: 600; }
  &__choices { display: grid; gap: 10px; }
  &__secondary { text-decoration: none; }
}
</style>
